import type { GetUsersResponseType } from "@nlc/shared-types";

import { USER_CATEGORIES, USER_ROLES } from "@nlc/shared-types";
import { useQuery } from "@tanstack/react-query";

import { usersKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

export function useGetUsers({
  category,
  direction,
  globalMode = false,
  itemsPerPage,
  page,
  search,
  sortBy,
}: {
  category?: string;
  direction: "asc" | "desc";
  globalMode?: boolean;
  itemsPerPage: number;
  page: number;
  search?: string;
  sortBy: string;
}) {
  const { data: user } = useUser();
  const projectId = user?.currentProjectId;
  const userRole = user?.user?.roleName;

  const shouldUseGlobalMode =
    globalMode &&
    userRole &&
    [USER_ROLES.SUPER_ADMIN, USER_ROLES.TEAM_LEADER].includes(userRole);

  const shouldExcludeProjectId =
    userRole === USER_ROLES.TEAM_LEADER &&
    category === USER_CATEGORIES.CUSTOMERS;

  const effectiveProjectId =
    projectId && !shouldExcludeProjectId ? projectId : undefined;

  const searchParams = {
    category,
    direction,
    globalMode: shouldUseGlobalMode || undefined,
    itemsPerPage,
    page,
    projectId: effectiveProjectId,
    search,
    sortBy,
  };

  const shouldEnableQuery =
    userRole &&
    [
      USER_ROLES.ADMIN,
      USER_ROLES.CUSTOMER_SUCCESS,
      USER_ROLES.OWNER,
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.TEAM_LEADER,
    ].includes(userRole);

  return useQuery<GetUsersResponseType>({
    enabled: Boolean(shouldEnableQuery),
    queryFn: async () => {
      const params = new URLSearchParams({
        direction,
        itemsPerPage: itemsPerPage.toString(),
        page: page.toString(),
        sortBy,
        ...(search && { search }),
        ...(category && { category }),
        ...(shouldUseGlobalMode && { globalMode: "true" }),
        ...(effectiveProjectId && { projectId: effectiveProjectId.toString() }),
      });

      return await apiClient.url(`/users?${params.toString()}`).get().json();
    },
    queryKey: usersKeys.usersList(searchParams),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}
